'use client';

import { cn } from '@/lib/utils';
import { CHAT_MODELS, type ChatModelId } from '@/lib/ai/chat-models';

/**
 * »Made in Slovenia« badge za GaMS (ali custom) model.
 *
 * - `variant="header"`: stalno viden ob wordmarku, ko je izbran slovenski model.
 * - `variant="message"`: drobna provenienca ob AI odgovoru (iz message.model).
 *
 * Zastavica je vgrajen SVG (brez zunanjih assetov, CSP-varno). Prej emoji 🇸🇮: Windows (Chrome, Edge,
 * Firefox) zastavic ne izrisuje in pokaže črki »SI« (regional indicator brez glifa), zato SVG (22. 10. 2026).
 */

/** Slovenska zastavica kot vgrajen SVG (poenostavljen grb: ščit, Triglav, tri zvezde). Dekorativna. */
export function SiFlag({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 10"
      aria-hidden
      focusable="false"
      className={cn('inline-block shrink-0 rounded-[1px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.25)]', className)}
    >
      <rect width="20" height="10" fill="#ffffff" />
      <rect y="3.333" width="20" height="3.334" fill="#005DA4" />
      <rect y="6.667" width="20" height="3.333" fill="#ED1C24" />
      <g transform="translate(3.1 0.9) scale(0.42)">
        <path d="M0 0H8V6.5Q8 9.3 4 10.6Q0 9.3 0 6.5Z" fill="#005DA4" stroke="#ED1C24" strokeWidth="0.7" />
        <path d="M0.7 8.2L2.4 5.6L3.2 6.6L4 4.6L4.8 6.6L5.6 5.6L7.3 8.2Z" fill="#ffffff" />
        <circle cx="2.4" cy="2.3" r="0.55" fill="#FFDE00" />
        <circle cx="5.6" cy="2.3" r="0.55" fill="#FFDE00" />
        <circle cx="4" cy="3.9" r="0.55" fill="#FFDE00" />
      </g>
    </svg>
  );
}
export function ModelBadge({
  modelId,
  variant = 'message',
  className,
}: {
  modelId: ChatModelId | undefined;
  variant?: 'header' | 'message';
  className?: string;
}) {
  if (!modelId) return null;
  const meta = CHAT_MODELS[modelId];
  if (!meta || !meta.slovenian) return null;

  if (variant === 'header') {
    return (
      <span
        title={`Poganja slovenski jezikovni model: ${meta.label}`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-primary/30',
          'bg-primary/5 px-2 py-0.5 text-[10px] font-semibold tracking-wide',
          'text-primary',
          className
        )}
      >
        <SiFlag className="h-3 w-[18px]" />
        <span>{meta.shortLabel}</span>
        <span className="hidden sm:inline text-muted-foreground font-normal">· Made in Slovenia</span>
      </span>
    );
  }

  return (
    <span
      title={`Odgovor generiral slovenski model: ${meta.label}`}
      className={cn(
        'inline-flex items-center gap-0.5 text-[8px] font-semibold uppercase tracking-wider',
        'text-primary/80 leading-none',
        className
      )}
    >
      <SiFlag className="h-2.5 w-[15px]" />
      {meta.shortLabel}
    </span>
  );
}
